import { IError } from "../types/errors";
import { ActionType } from "../types/store-types";
import { IField } from "./../../store/fields/types";

export interface IPage {
    _id: string;
    fields: IField["_id"][];
    title: string;
    temp_id?: string;
}

/* - Data Types - */

export interface IInitPagesData {
    list: IPage[];
}

export interface IChangePageData {
    data: IPage[];
}

export interface IChangePageDataFailure extends IError {}

export interface IChangePageDataSuccess {}

export interface IChangeCurrentPageData {
    id: IPage["_id"];
}
export interface IRemoveData {
    pageId: IPage["_id"];
}

export interface IAddFieldToPageData {
    pageId: IPage["_id"];
    fieldId: IField["_id"];
}

export interface IReplaceFieldIdData {
    tempId: IField["_id"];
    fieldId: IField["_id"];
}

export interface IDeleteFieldData {
    pageId: IPage["_id"];
    fieldId: IField["_id"];
}

export interface IDuplicateFieldData {
    pageId: IPage["_id"];
    fieldId: IField["_id"];
    newFieldId?: IField["_id"];
    index: number;
}

/* -- Create Page Data --  */

export interface ICreatePageByTempIdData {
    page: IPage;
}

export interface ICreatePageRequestData {}

export interface ICreatePageSuccessData {
    _id: IPage["_id"];
    tempId: IPage["_id"];
}

export interface ICreatePageFailureData extends IError {
    id: IPage["_id"];
}

/* -- Delete Page Data --  */

export interface IDeletePageRequestData {
    id: IPage["_id"];
    removeFields: boolean;
}

export interface IDeletePageSuccessData {
    id: IPage["_id"];
}

export interface IDeletePageFailureData extends IError {}

/* -- Change Page Order Data --  */

export interface IChangeOrderRequestData {
    pageId: IPage["_id"];
    dropIndex: number;
    dragIndex: number;
}

export interface IChangeOrderSuccessData {}

export interface IChangeOrderFailureData extends IError {}

/* -- Update Page Data -- */

export interface IUpdatePageData {
    _id: IPage["_id"];
    title: IPage["title"];
}

export interface IUpdatePageFailureData extends IError {
    id: IPage["_id"];
}

export interface IUpdatePageSuccessData {}

export interface IRefreshPages {}

/* -- State -- */

export interface IPagesState {
    list: IPage[];
    current: IPage["_id"] | null;
    isLoading: boolean;
    error: IError | null;
}

/* - Action Types - */

export enum PagesActionTypes {
    INIT_PAGES = "PAGES.INIT_PAGES",
    CHANGE_CURRENT_PAGE = "PAGES.CHANGE_CURRENT_PAGE",
    REMOVE = "PAGES.REMOVE",
    ADD_FIELD_TO_PAGE = "PAGES.ADD_FIELD_TO_PAGE",
    REPLACE_FIELD_ID = "PAGES.REPLACE_FIELD_ID",
    DELETE_FIELD = "PAGES.DELETE_FIELD",
    DUPLICATE_FIELD = "PAGES.DUPLICATE_FIELD",

    CREATE_PAGE_BY_TEMP_ID = "PAGES.CREATE_PAGE_BY_TEMP_ID",
    CREATE_PAGE_REQUEST = "PAGES.CREATE_PAGE_REQUEST",
    CREATE_PAGE_SUCCESS = "PAGES.CREATE_PAGE_SUCCESS",
    CREATE_PAGE_FAILURE = "PAGES.CREATE_PAGE_FAILURE",

    DELETE_PAGE_REQUEST = "PAGES.DELETE_PAGE_REQUEST",
    DELETE_PAGE_SUCCESS = "PAGES.DELETE_PAGE_SUCCESS",
    DELETE_PAGE_FAILURE = "PAGES.DELETE_PAGE_FAILURE",

    CHANGE_PAGE_ORDER_REQUEST = "PAGES.CHANGE_PAGE_ORDER_REQUEST",
    CHANGE_PAGE_ORDER_SUCCESS = "PAGES.CHANGE_PAGE_ORDER_SUCCESS",
    CHANGE_PAGE_ORDER_FAILURE = "PAGES.CHANGE_PAGE_ORDER_FAILURE",

    UPDATE_PAGE_DATA_REQUEST = "PAGES.UPDATE_PAGE_DATA_REQUEST",
    UPDATE_PAGE_DATA_FAILURE = "PAGES.UPDATE_PAGE_DATA_FAILURE",
    UPDATE_PAGE_DATA_SUCCESS = "PAGES.UPDATE_PAGE_DATA_SUCCESS",

    UPDATE_PAGE_REQUEST = "PAGES.UPDATE_PAGE_REQUEST",
    UPDATE_PAGE_FAILURE = "PAGES.UPDATE_PAGE_FAILURE",
    UPDATE_PAGE_SUCCESS = "PAGES.UPDATE_PAGE_SUCCESS",

    REFRESH_PAGES = "PAGES.REFRESH_PAGES",
}

/* - Action Interfaces  - */

export type InitPagesActionType = ActionType<PagesActionTypes.INIT_PAGES, IInitPagesData>;

export type ChangeCurrentPageActionType = ActionType<PagesActionTypes.CHANGE_CURRENT_PAGE, IChangeCurrentPageData>;

export type RemoveActionType = ActionType<PagesActionTypes.REMOVE, IRemoveData>;

export type AddFieldToPageActionType = ActionType<PagesActionTypes.ADD_FIELD_TO_PAGE, IAddFieldToPageData>;

export type ReplaceFieldIdActionType = ActionType<PagesActionTypes.REPLACE_FIELD_ID, IReplaceFieldIdData>;

export type DeleteFieldActionType = ActionType<PagesActionTypes.DELETE_FIELD, IDeleteFieldData>;

export type DuplicateFieldActionType = ActionType<PagesActionTypes.DUPLICATE_FIELD, IDuplicateFieldData>;

/* -- Create Page Actions -- */

export type CreatePageByTempIdActionType = ActionType<PagesActionTypes.CREATE_PAGE_BY_TEMP_ID, ICreatePageByTempIdData>;
export type CreatePageRequestActionType = ActionType<PagesActionTypes.CREATE_PAGE_REQUEST, ICreatePageRequestData>;
export type CreatePageSuccessActionType = ActionType<PagesActionTypes.CREATE_PAGE_SUCCESS, ICreatePageSuccessData>;
export type CreatePageFailureActionType = ActionType<PagesActionTypes.CREATE_PAGE_FAILURE, ICreatePageFailureData>;

/* -- Delete Page Actions -- */

export type DeletePageRequestActionType = ActionType<PagesActionTypes.DELETE_PAGE_REQUEST, IDeletePageRequestData>;
export type DeletePageSuccessActionType = ActionType<PagesActionTypes.DELETE_PAGE_SUCCESS, IDeletePageSuccessData>;
export type DeletePageFailureActionType = ActionType<PagesActionTypes.DELETE_PAGE_FAILURE, IDeletePageFailureData>;

/* -- Update Page Order Actions -- */

export type ChangePageOrderRequestActionType = ActionType<
    PagesActionTypes.CHANGE_PAGE_ORDER_REQUEST,
    IChangeOrderRequestData
>;
export type ChangePageOrderSuccessActionType = ActionType<
    PagesActionTypes.CHANGE_PAGE_ORDER_SUCCESS,
    IChangeOrderSuccessData
>;
export type ChangePageOrderFailureActionType = ActionType<
    PagesActionTypes.CHANGE_PAGE_ORDER_FAILURE,
    IChangeOrderFailureData
>;

/* -- Update Page Data Actions -- */
export type UpdatePageDataRequestActionType = ActionType<PagesActionTypes.UPDATE_PAGE_DATA_REQUEST, IChangePageData>;

export type UpdatePageDataSuccessActionType = ActionType<
    PagesActionTypes.UPDATE_PAGE_DATA_SUCCESS,
    IChangePageDataSuccess
>;

export type UpdatePageDataFailureActionType = ActionType<
    PagesActionTypes.UPDATE_PAGE_DATA_FAILURE,
    IChangePageDataFailure
>;

/* -- Update Page Actions -- */

export type UpdatePageRequestActionType = ActionType<PagesActionTypes.UPDATE_PAGE_REQUEST, IUpdatePageData>;

export type UpdatePageFailureActionType = ActionType<PagesActionTypes.UPDATE_PAGE_FAILURE, IUpdatePageFailureData>;

export type UpdatePageSuccessActionType = ActionType<PagesActionTypes.UPDATE_PAGE_SUCCESS, IUpdatePageSuccessData>;

export type RefreshPagesActionType = ActionType<PagesActionTypes.REFRESH_PAGES , IRefreshPages>